import { Model } from '@vuex-orm/core'
import { File } from 'src/store/models/channel/index.js'
import { Channel } from 'src/store/models/index.js'
import Channels from 'src/api/channels.js'
import constants from 'src/config/constants.ts'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export class Message extends Model {
  static entity = 'message'
  static primaryKey = 'uuid'

  static fields () {
    return {
      id: this.attr(null),
      uuid: this.string(null),
      chat_id: this.number(null),
      user_id: this.number(null),
      file_uuid: this.string(null),
      reply_uuid: this.string(null),
      original_id: this.number(null),
      type: this.attr(null),
      label: this.string(''),
      content_rendered: this.attr(''),
      content: this.string(''),
      forward_uuid: this.string(''),
      forwarded: this.attr(null),
      is_unset: this.attr(null),
      unset_at: this.attr(null),
      created_at: this.attr(null),
      updated_at: this.attr(null),
      deleted_at: this.attr(null),
      user: this.attr({}),
      file: this.belongsTo(File, 'file_uuid'),
      reply: this.attr(null),
      can: this.attr({}),
      meta: this.attr({}),
      target: this.attr(null),
      target_snapshot: this.attr(null),
      target123123: this.attr(null),
      preview: this.attr(null),
      target_type: this.string(''),
      survey_form_id: this.attr(null),
      survey_uuid: this.attr(null),
      survey: this.attr(null),
      payload: this.attr({}),
      actions: this.attr([]),
      is_read: this.attr(null),
      source_language: this.attr('')
    }
  }

  static async fetchByChannel (id, page, only_unseen = false) {
    const res = await Channels.getChannelChatByIdMessagesPaginated(id, page, only_unseen)
    await this.insertOrUpdate({ data: res.data })

    if (!res.meta?.participants) {
      return res
    }

    const chatId = Number(id)
    const channel = Channel.query().where('chat_id', chatId).first() ||
      Channel.all().find(c => Number(c.chat_id) === chatId)

    if (!channel) return res

    const updatedChannel = {
      ...channel,
      meta: {
        ...(channel.meta || {}),
        participants: res.meta.participants
      }
    }
    await Channel.insertOrUpdate({ data: updatedChannel })

    return res
  }

  static async markNotificationAsRead (messageUuid) {
    const res = await Channels.putNotificationStatusSeen(messageUuid)
    await this.insertOrUpdate({ data: res.data })
  }

  static async markNotificationAsUnread (messageUuid) {
    const res = await Channels.putNotificationStatusUnseen(messageUuid)
    await this.insertOrUpdate({ data: res.data })
  }

  static async deleteSingleNotification () {
    // TODO https://sdui.atlassian.net/browse/SP-5276
  }

  static async deleteAllNotifications () {
    // TODO https://sdui.atlassian.net/browse/SP-5276
  }

  static async updateLocally (data) {
    const message = data.message
    if (!message?.uuid) return
    const existingMessage = Message.find(message.uuid)

    if (!existingMessage) return

    const updatedMessage = { ...existingMessage, ...message }

    await Message.insertOrUpdate({ data: updatedMessage })
  }

  static async deleteItem (chatId, uuid) {
    try {
      const res = await Channels.deleteChannelChatByIdMessageById(chatId, uuid, { hideSuccessMessages: true })
      this.insertOrUpdate({ data: res.data })
      return res
    } catch {
      return false
    }
  }

  static async endConference (channelId, uuid) {
    const res = await Channels.postChannelsByIdEndConferencesById(channelId, uuid, { hideSuccessMessages: true })
    const targetMsg = await Message.query()
      .where('target', s => s?.uuid === uuid)
      .first()

    if (!targetMsg || !res.data) return

    const updatedMsg = { ...targetMsg, target: res.data }
    await Message.insertOrUpdate({ data: updatedMsg })
  }

  static async getReadersList (chatId, uuid, page, search) {
    try {
      return Channels.getChannelChatByIdReadersPaginatedSearch(chatId, uuid, page, search)
    } catch {
      return null
    }
  }

  static async updateFile (data) {
    try {
      const fileData = await File.getDetails(data.uuid, data.cloud_id)

      const targetMsg = await Message.query()
        .where('file_uuid', data.uuid)
        .first()

      const updatedMsg = { ...targetMsg, file: { ...fileData } }
      await Message.insertOrUpdate({ data: GlobalPrototypes.clone(updatedMsg) })
    } catch {}
  }

  static async updateSurvey (messageUuid, survey) {
    await Message.update({
      where: (msg) => {
        return msg.uuid === messageUuid
      },
      data: { target: survey }
    })
  }

  static async updateConferenceFromBroadcast (message) {
    await Message.update({
      where: (msg) => {
        return msg.target?.uuid === message.uuid
      },
      data: { target: message }
    })
  }

  static async updateSurveyFromBroadcast (survey) {
    const targetMsg = await Message.query()
      .where('target_type', constants.message.targetTypes.SURVEYS)
      .where('target', t => t?.id === survey.id)
      .first()

    await Message.update({
      where: (msg) => {
        if (msg.target_type === constants.message.targetTypes.SURVEYS) return msg.target?.id === survey.id
        return msg.target?.uuid === survey.uuid
      },
      data: { target: {
        ...survey,
        // BE is sending the wrong value for "is_user_voted", causing the survey the show the submit button even though it was
        // already voted on. This is a quick-fix.
        ...(targetMsg?.target) && {is_user_voted: targetMsg.target.is_user_voted}}
      }
    })
  }

  static async getConference (channelId, conferenceUuid) {
    return Channels.getChannelsByIdConferencesById(channelId, conferenceUuid)
  }

  static async searchAcrossChannels (page, search) {
    return Channels.getChannelChatMessagesSearch(page, search)
  }
}

export default Message
